import { useState, useEffect } from 'react';

import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@realtyka/real-ui';

import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToSquare } from '@fortawesome/pro-regular-svg-icons';
import LogoutButton from '../components/Button';
import {
  fetchKeymakerCurrentUser,
  sendEmailVerificationToken,
} from '../slices/AuthSlice';
import { AppDispatch, RootState } from '../types';
import { useCountdown } from '../utils/useCountdown';
import OneRealLogo from '../assets/img/auth/auth-one-real-logo.png';
import RealBrandLogo from '../assets/img/real-brand-logo-black-border.svg';
import { useLogout } from '../hooks/useLogout';

interface EmailVerificationRequiredProps {}

const EmailVerificationRequired: React.FC<
  EmailVerificationRequiredProps
> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const dispatch: AppDispatch = useDispatch();
  const isAgentContext = location.pathname === '/agent-verify-email';
  const { keymakerCurrentUser } = useSelector((state: RootState) => state.auth);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { seconds, start } = useCountdown({
    initialSeconds: 60,
    onComplete: () => setIsButtonDisabled(false),
  });

  const handleLogoutAndRedirect = async () => {
    await logout();
    navigate(isAgentContext ? '/agent-login' : '/login');
  };

  useEffect(() => {
    const fetchUser = async () => {
      const user = await dispatch(fetchKeymakerCurrentUser());
      if (!user) {
        await handleLogoutAndRedirect();
      }
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (keymakerCurrentUser?.confirmEmailLinkSentAt) {
      const sentTime = DateTime.fromMillis(
        keymakerCurrentUser.confirmEmailLinkSentAt,
      );
      const now = DateTime.now();
      const diffInMinutes = now.diff(sentTime, 'minutes').minutes;

      if (diffInMinutes < 2) {
        setEmailSent(true);
        setIsButtonDisabled(true);
        start();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keymakerCurrentUser?.confirmEmailLinkSentAt]);

  const handleSendVerification = async () => {
    if (isButtonDisabled) {
      return;
    }

    setIsSending(true);
    const isSent = await dispatch(sendEmailVerificationToken());
    if (isSent) {
      setEmailSent(true);
      setIsButtonDisabled(true);
      start();
      await dispatch(fetchKeymakerCurrentUser());
    } else {
      setIsButtonDisabled(false);
      await handleLogoutAndRedirect();
    }
    setIsSending(false);
  };

  return (
    <div className='lg:col-span-1 place-content-start lg:bg-none bg-no-repeat bg-contain bg-white h-screen py-4 relative'>
      <div className='lg:h-full auth-custom-scroll'>
        <div className='block px-4 lg:px-16 pt-6 lg:pt-16 pb-6'>
          <img
            src={isAgentContext ? RealBrandLogo : OneRealLogo}
            alt='placeholder'
            width={isAgentContext ? 90 : 170}
            height={50}
          />
        </div>
        <div className='flex flex-col items-center bg-white px-4 py-6 mx-4 rounded-xl lg:mt-20 lg:mb-20 mt-0 pb-24 lg:pb-6'>
          <div className='flex flex-col flex-grow justify-center items-center m-6 lg:mx-auto'>
            <div className='flex flex-col items-center bg-white p-4 md:p-10 gap-6 rounded-xl w-full lg:w-[520px] xl:w-[664px] text-center'>
              <h1 className='text-2xl font-bold'>
                Email Verification Required
              </h1>
              <p className='text-gray-600'>
                In order to continue using ReZEN, you must verify your email
                address.
                {keymakerCurrentUser?.confirmEmailLinkSentAt && (
                  <b className='block mt-2 text-black'>
                    A verification link was sent on{' '}
                    {DateTime.fromMillis(
                      keymakerCurrentUser.confirmEmailLinkSentAt,
                    ).toFormat("LLL d, yyyy 'at' h:mm a")}
                    .
                  </b>
                )}
              </p>
              <Button
                onClick={handleSendVerification}
                disabled={isButtonDisabled ?? isSending}
                type='button'
                variant='outline'
                color='secondary'
              >
                <span className='px-3.5'>
                  {isSending ? 'Sending...' : 'Resend Verification Email'}
                </span>
              </Button>
              {emailSent && (
                <p className='text-green-600'>
                  Please check your inbox and click the verification link to
                  complete the process. If you do not see the email, please
                  check your spam folder.
                </p>
              )}
              {seconds > 0 && (
                <p className='text-sm text-gray-500'>
                  You can request another verification email in {seconds}{' '}
                  seconds.
                </p>
              )}
              <p className='text-gray-600'>
                Once you have verified your email, please return here and click
                Logout. You will be prompted to log back into your account.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className='flex justify-end fixed bottom-10 right-10'>
        <LogoutButton
          type='button'
          label='Logout'
          leftIcon={
            <FontAwesomeIcon
              size='lg'
              icon={faArrowDownToSquare}
              className='-rotate-90 mr-1 text-white'
            />
          }
          onPress={async () => {
            await logout();
            navigate(isAgentContext ? '/agent-login' : '/login');
          }}
        />
      </div>
    </div>
  );
};

export default EmailVerificationRequired;
